import {
  isAssessmentSlotRequested,
  type AssessmentSchemes,
} from '@/lib/referral/assessmentSchemes';

type ReferralFileSources = {
  ppr_documents?: object | null;
  hospital_documents?: object | null;
  paper_anamnese_forms?: object | null;
  school_completion_forms?: object | null;
  assessment_schemes?: AssessmentSchemes;
};

export type ReferralFileSlot = {
  key: string;
  category: string;
  fieldName: string;
  label: string;
  group: string;
};

function slot(
  category: string,
  fieldName: string,
  label: string,
  group: string,
): ReferralFileSlot {
  return {
    key: `${category}:${fieldName}`,
    category,
    fieldName,
    label,
    group,
  };
}

/** All checklist slots that support file upload on Sagsakter. */
export const ALL_REFERRAL_FILE_SLOTS: ReferralFileSlot[] = [
  // PPR
  slot('ppr', 'wisc_v_forside', 'WISC V forside eller computerudskrift', 'PPR'),
  slot('ppr', 'wisc_v_rapport', 'WISC V rapport', 'PPR'),
  slot('ppr', 'wppsi_iv_forside', 'WPPSI-IV forside', 'PPR'),
  slot('ppr', 'wppsi_iv_rapport', 'WPPSI-IV rapport', 'PPR'),
  // Hospital
  slot('hospital', 'journaludskrift', 'Journaludskrift', 'Hospital'),
  slot('hospital', 'scannings_svar_mr', 'Scannings-svar · MR', 'Hospital'),
  slot('hospital', 'scannings_svar_ct', 'Scannings-svar · CT', 'Hospital'),
  // Paper anamnese
  slot('paper_anamnese', 'smaaboern', 'Småbørn', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'smaaboern_genhenvisning', 'Småbørn genhenvisning', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'skoleboern', 'Skolebørn', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'skoleboern_genhenvisning', 'Skolebørn genhenvisning', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'unge_15_plus', 'Unge 15+', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'unge_15_plus_genhenvisning', 'Unge 15+ genhenvisning', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'tillaeg_til_anamnese_13_18', 'Tillæg til anamneseskema (13-18 år)', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'nordisk_sporgeskema_2_5', 'Nordisk spørgeskema (2-5 år)', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'dupaul_foraeldre_skole', 'DUPAUL forældre // skole', 'Papir Anamneseskema'),
  // School
  slot('school_completion', 'førskole', 'Førskole', 'Skema til udfyldelse'),
  slot('school_completion', 'indskoling', 'Indskoling', 'Skema til udfyldelse'),
  slot('school_completion', 'mellemtrin', 'Mellemtrin', 'Skema til udfyldelse'),
  slot('school_completion', 'udskoling', 'Udskoling', 'Skema til udfyldelse'),
  // Assessments
  slot('assessment', 'abas_3_førskole_foraeldre', 'ABAS-3 førskole · Forældre', 'Assessments'),
  slot('assessment', 'abas_3_førskole_paedagog', 'ABAS-3 førskole · Pædagog', 'Assessments'),
  slot('assessment', 'abas_3_førskole_foraeldre_paedagog', 'ABAS-3 førskole (forældre/pædagog)', 'Assessments'),
  slot('assessment', 'abas_3_skole_foraeldre', 'ABAS-3 skole · Forældre', 'Assessments'),
  slot('assessment', 'abas_3_skole_laerer', 'ABAS-3 skole · Lærer', 'Assessments'),
  slot('assessment', 'abas_3_skole_foraeldre_laerer', 'ABAS-3 skole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'abas_3_voksen_selv', 'ABAS-3 voksen · Selv', 'Assessments'),
  slot('assessment', 'abas_3_voksen_paaroerende', 'ABAS-3 voksen · Andre', 'Assessments'),
  slot('assessment', 'abas_3_voksen_selv_paaroerende', 'ABAS-3 voksen (selv/andre)', 'Assessments'),
  slot('assessment', 'brief_2_førskole_foraeldre', 'BRIEF-2 førskole · Forældre', 'Assessments'),
  slot('assessment', 'brief_2_førskole_paedagog', 'BRIEF-2 førskole · Pædagog', 'Assessments'),
  slot('assessment', 'brief_2_førskole_foraeldre_paedagog', 'BRIEF-2 førskole (forældre/pædagog)', 'Assessments'),
  slot('assessment', 'brief_2_skole_foraeldre', 'BRIEF-2 skole · Forældre', 'Assessments'),
  slot('assessment', 'brief_2_skole_laerer', 'BRIEF-2 skole · Lærer', 'Assessments'),
  slot('assessment', 'brief_2_skole_foraeldre_laerer', 'BRIEF-2 skole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'brief_selv', 'BRIEF 2 selv', 'Assessments'),
  slot('assessment', 'brief_2_voksen_selv', 'BRIEF-2 voksen · Selv', 'Assessments'),
  slot('assessment', 'brief_2_voksen_paaroerende', 'BRIEF-2 voksen · Pårørende', 'Assessments'),
  slot('assessment', 'brief_2_voksen_selv_paaroerende', 'BRIEF-2 voksen (selv/pårørende)', 'Assessments'),
  slot('assessment', 'brief_srs_multi_besvarelse', 'BRIEF + SRS multi (besvarelse)', 'Assessments'),
  slot('assessment', 'srs_førskole_foraeldre', 'SRS førskole · Forældre', 'Assessments'),
  slot('assessment', 'srs_førskole_laerer', 'SRS førskole · Lærer', 'Assessments'),
  slot('assessment', 'srs_førskole_foraeldre_laerer', 'SRS førskole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'srs_skole_foraeldre', 'SRS skole · Forældre', 'Assessments'),
  slot('assessment', 'srs_skole_laerer', 'SRS skole · Lærer', 'Assessments'),
  slot('assessment', 'srs_skole_foraeldre_laerer', 'SRS skole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'vineland_førskole', 'VINELAND førskole', 'Assessments'),
  slot('assessment', 'vineland_førskole_foraeldre', 'VINELAND førskole · Forældre', 'Assessments'),
  slot('assessment', 'vineland_førskole_laerer', 'VINELAND førskole · Lærer', 'Assessments'),
  slot('assessment', 'vineland_førskole_foraeldre_laerer', 'VINELAND førskole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'vineland_skole', 'VINELAND skole', 'Assessments'),
  slot('assessment', 'vineland_skole_foraeldre', 'VINELAND skole · Forældre', 'Assessments'),
  slot('assessment', 'vineland_skole_laerer', 'VINELAND skole · Lærer', 'Assessments'),
  slot('assessment', 'vineland_skole_foraeldre_laerer', 'VINELAND skole (forældre/lærer)', 'Assessments'),
];

function isChecked(
  obj: Record<string, unknown> | null | undefined,
  field: string,
): boolean {
  return Boolean(obj?.[field]);
}

/** Slots that are checked on Sagsakter (need files). */
export function getRequestedFileSlots(referral: ReferralFileSources): ReferralFileSlot[] {
  const ppr = referral.ppr_documents;
  const hospital = referral.hospital_documents;
  const paper = referral.paper_anamnese_forms;
  const school = referral.school_completion_forms;
  const assessment = referral.assessment_schemes;

  return ALL_REFERRAL_FILE_SLOTS.filter((s) => {
    switch (s.category) {
      case 'ppr':
        return isChecked(ppr as Record<string, unknown> | null | undefined, s.fieldName);
      case 'hospital':
        return isChecked(hospital as Record<string, unknown> | null | undefined, s.fieldName);
      case 'paper_anamnese':
        return isChecked(paper as Record<string, unknown> | null | undefined, s.fieldName);
      case 'school_completion':
        return isChecked(school as Record<string, unknown> | null | undefined, s.fieldName);
      case 'assessment':
        return isAssessmentSlotRequested(assessment, s.fieldName);
      default:
        return false;
    }
  });
}

export function findSlot(
  category: string | null | undefined,
  fieldName: string | null | undefined,
): ReferralFileSlot | undefined {
  if (!category || !fieldName) return undefined;
  return ALL_REFERRAL_FILE_SLOTS.find(
    (s) => s.category === category && s.fieldName === fieldName,
  );
}
